import React, { useEffect, useState } from "react";
import { fetcher } from "../../../services/api";
import DotsIcon from "../../../assets/icons/dots";
import ManageContentModal from "../ManageContentModal";
import Loader from "../../Loader/Loader";
import "./ManageContentTable.css";
import HeartIcon from "../../../assets/icons/heart";
import ApiNoContent from "../../ApiKeys/ApiNoContent";

type ClientsDataType = {
  key: string;
  created_at: string;
  groupName: string;
  blackListedWordsCount: number;
  blackListedWords: string[];
  black_listed_words_by_country: {
    [countryCode: string]: string[];
  };
  contentFilterTypes: string[];
};

const NotFoundComponent: React.FC = () => (
   <ApiNoContent
    icon={<HeartIcon />}
    iconClassName="api-keys__heart-icon"
    title="Content Not Found Yet"
    description=""
    buttonText="Get Started"
    buttonType="redirect"
    onButtonClick={() => window.location.href = "/api-keys"}
    />
);

const ManageContentTable: React.FC = () => {
  const [groupsData, setGroupsData] = useState<ClientsDataType[]>([]);
  const [contentFilterTypes, setContentFilterTypes] = useState<{
    [key: number]: string;
  }>({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<ClientsDataType | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const fetchContentFilterTypes = async () => {
      try {
        const res = await fetcher<{
          data: { id: number; display_name: string }[];
        }>("/publisher/client/content-filter-types", {
          method: "GET",
        });

        if (res?.data?.data) {
          const filterTypes = res.data.data.reduce(
            (
              acc: { [key: number]: string },
              type: { id: number; display_name: string }
            ) => {
              acc[type.id] = type.display_name;
              return acc;
            },
            {}
          );
          setContentFilterTypes(filterTypes);
        } else {
          console.error("Invalid response for content filter types:", res);
          setHasError(true);
        }
      } catch (error) {
        console.error("Failed to fetch content filter types:", error);
        setHasError(true);
      }
    };

    fetchContentFilterTypes();
  }, []);

  useEffect(() => {
    const fetchClientsData = async () => {
      try {
        setIsLoading(true);
        const res = await fetcher<{ data: { data: { data: any[] } } }>(
          "/publisher/client",
          {
            method: "GET",
          }
        );

        const groups = res?.data?.data?.data;
        const groupsData = Array.isArray(groups)
          ? groups.flatMap((app: any) => ({
              key: app.key,
              created_at: app.created_at,
              groupName: app.name,
              blackListedWordsCount: app.black_listed_words?.length || 0,
              blackListedWords: app.black_listed_words,
              black_listed_words_by_country: app.black_listed_words_by_country,
              contentFilterTypes: (app.default_content_filter_type || []).map(
                (code: number) => contentFilterTypes[code]
              ),
            }))
          : [];

        setGroupsData(groupsData);
        setHasError(false);
      } catch (error) {
        console.error("Failed to fetch client data:", error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (Object.keys(contentFilterTypes).length > 0 || hasError) {
      fetchClientsData();
    }
  }, [contentFilterTypes, hasError, selectedGroup]);

  const renderContent = () => {
    if (isLoading) {
      return <Loader />;
    }

    if (hasError || groupsData.length === 0) {
      return <NotFoundComponent />;
    }

    return (
      <table className="content-management-table">
        <thead>
          <tr>
            <th>Platform Name</th>
            <th>Content Type</th>
            <th>N of Keywords</th>
            <th>Updated On</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {groupsData.map((group) => (
            <tr key={group.key}>
              <td>{group.groupName}</td>
              <td>{group.contentFilterTypes.join(", ")}</td>
              <td>{group.blackListedWordsCount} KEYWORDS</td>
              <td>{new Date(group.created_at).toLocaleDateString()}</td>
              <td>
                <button
                  aria-label="Edit Details"
                  onClick={() => {
                    setSelectedGroup(group);
                    setIsModalVisible(true);
                  }}
                >
                  <DotsIcon />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="manage-content">
      <h2 className="subtitle">Manage your content by application</h2>
      <div className="table-container no-padding">{renderContent()}</div>
      <ManageContentModal
        isVisible={isModalVisible}
        setIsVisible={() => setIsModalVisible(!isModalVisible)}
        data={selectedGroup}
        onSave={(updatedGroup) => {
          setGroupsData((prevData) =>
            prevData.map((group) =>
              group.key === updatedGroup.key ? updatedGroup : group
            )
          );
        }}
      />
    </div>
  );
};

export default ManageContentTable;
